"use client"

import { useEffect, useRef } from "react"

import { trackEvent, type EventProperties } from "@/lib/analytics"

/**
 * Fire a one-shot `service_lead_cta_viewed` impression the first time an Angi
 * referral / service-lead CTA is meaningfully visible. Returns a ref to attach
 * to the CTA root element.
 *
 * Pair the SAME `source_placement` with the CTA's `service_lead_angi_clicked`
 * click event so PostHog can join impressions to clicks and compute per-surface
 * CTR. Before this, `DiagnosisProReferralCard` and `AngiReferralCard` fired
 * click-only, so their CTR was unmeasurable (impressions never landed in
 * `service_lead_cta_viewed`).
 *
 * Uses a raw IntersectionObserver with a `typeof … === 'undefined'` fallback
 * (fire immediately) so the impression is never silently dropped in an
 * environment without an observer — matching the inline observer in
 * `ServiceQuoteCTA` and the `diagnosis_gate` surface tracker.
 */
export function useServiceLeadCtaViewed<T extends HTMLElement = HTMLDivElement>(
  props: EventProperties
) {
  const ref = useRef<T | null>(null)
  const fired = useRef(false)
  const propsRef = useRef(props)
  propsRef.current = props

  useEffect(() => {
    const node = ref.current
    if (!node || fired.current) return

    const fire = () => {
      if (fired.current) return
      fired.current = true
      trackEvent("service_lead_cta_viewed", propsRef.current)
    }

    if (typeof IntersectionObserver === "undefined") {
      fire()
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          fire()
          observer.disconnect()
        }
      },
      { threshold: 0.35 }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return ref
}
